<template>
    <div class="page-container">
        <div class="settings-form">
            <div class="header-section">
                <h2 class="page-title">TX广告商数据管理</h2>

                <div class="sync-section">
                    <el-button type="primary" size="large" @click="syncAllAdvertisers" :loading="syncLoading"
                        :disabled="!hasAuthRecords">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        同步所有广告商数据
                    </el-button>

                    <div class="sync-status" v-if="syncStatus.last_sync">
                        <div class="status-item">
                            <span class="label">最后同步：</span>
                            <span class="value">{{ formatTime(syncStatus.last_sync.sync_time) }}</span>
                        </div>
                        <div class="status-item">
                            <span class="label">同步结果：</span>
                            <span class="value success" v-if="syncStatus.last_sync.success_count > 0">
                                成功 {{ syncStatus.last_sync.success_count }} 条
                            </span>
                            <span class="value fail" v-if="syncStatus.last_sync.fail_count > 0">
                                失败 {{ syncStatus.last_sync.fail_count }} 条
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 统计信息 -->
            <div class="stats-section">
                <el-row :gutter="20">
                    <el-col :span="6">
                        <div class="stat-card">
                            <div class="stat-icon total">
                                <el-icon>
                                    <Document />
                                </el-icon>
                            </div>
                            <div class="stat-content">
                                <div class="stat-number">{{ syncStatus.total_advertisers || 0 }}</div>
                                <div class="stat-label">总广告商数</div>
                            </div>
                        </div>
                    </el-col>
                    <el-col :span="6">
                        <div class="stat-card">
                            <div class="stat-icon normal">
                                <el-icon>
                                    <CircleCheck />
                                </el-icon>
                            </div>
                            <div class="stat-content">
                                <div class="stat-number">{{ statusCount.normal || 0 }}</div>
                                <div class="stat-label">正常状态</div>
                            </div>
                        </div>
                    </el-col>
                    <el-col :span="6">
                        <div class="stat-card">
                            <div class="stat-icon pending">
                                <el-icon>
                                    <Clock />
                                </el-icon>
                            </div>
                            <div class="stat-content">
                                <div class="stat-number">{{ statusCount.pending || 0 }}</div>
                                <div class="stat-label">待审核</div>
                            </div>
                        </div>
                    </el-col>
                    <el-col :span="6">
                        <div class="stat-card">
                            <div class="stat-icon rejected">
                                <el-icon>
                                    <CloseBold />
                                </el-icon>
                            </div>
                            <div class="stat-content">
                                <div class="stat-number">{{ statusCount.rejected || 0 }}</div>
                                <div class="stat-label">审核拒绝</div>
                            </div>
                        </div>
                    </el-col>
                </el-row>
            </div>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.keyword" placeholder="搜索企业名称、品牌名称、联系人" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-select v-model="searchForm.system_status" placeholder="状态筛选" clearable size="large"
                    style="width: 150px; margin-right: 15px">
                    <el-option label="全部状态" value=""></el-option>
                    <el-option label="正常" value="CUSTOMER_STATUS_NORMAL"></el-option>
                    <el-option label="待审核" value="CUSTOMER_STATUS_PENDING"></el-option>
                    <el-option label="审核拒绝" value="CUSTOMER_STATUS_DENIED"></el-option>
                </el-select>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="info" size="large" @click="resetSearch">
                    <el-icon>
                        <RefreshLeft />
                    </el-icon>
                    重置
                </el-button>
            </div>

            <!-- 广告商列表 -->
            <el-table :data="advertiserList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
                @sort-change="handleSortChange">
                <el-table-column prop="account_id" label="账户ID" width="120" sortable="custom" />
                <el-table-column prop="corporation_name" label="企业名称" min-width="200" show-overflow-tooltip />
                <el-table-column prop="brand_name" label="品牌名称" width="150" show-overflow-tooltip>
                    <template #default="{ row }">
                        <span v-if="row.brand_name">{{ row.brand_name }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column label="系统状态" width="100">
                    <template #default="{ row }">
                        <el-tag :type="getStatusType(row.system_status)" size="small">
                            {{ getStatusText(row.system_status) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="日预算" width="120" sortable="custom">
                    <template #default="{ row }">
                        <span v-if="row.daily_budget > 0">
                            {{ formatBudget(row.daily_budget) }}
                        </span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column prop="contact_person" label="联系人" width="120">
                    <template #default="{ row }">
                        <span v-if="row.contact_person">{{ row.contact_person }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column prop="contact_mobile" label="联系电话" width="150">
                    <template #default="{ row }">
                        <span v-if="row.contact_mobile">{{ row.contact_mobile }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column prop="industry_id" label="行业ID" width="120" />

                <el-table-column prop="auth_name" label="授权账号" width="150" show-overflow-tooltip />

                <el-table-column prop="last_sync_time" label="最后同步" width="180" sortable="custom">
                    <template #default="{ row }">
                        {{ formatTime(row.last_sync_time) }}
                    </template>
                </el-table-column>

                <el-table-column label="操作" width="200" fixed="right">
                    <template #default="scope">
                        <el-button type="success" size="small" @click="openAdDialog(scope.row)">
                            <el-icon>
                                <View />
                            </el-icon>
                            查看广告
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
                            :loading="deleteLoading[scope.row.id]">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchAdvertiserList"
                    @current-change="fetchAdvertiserList" />
            </div>
        </div>

        <!-- 定向推广弹窗 -->
        <el-dialog v-model="previewDialogVisible" title="定向推广" width="800px" :close-on-click-modal="false">
            <el-tabs v-model="previewActiveTab" type="border-card">
                <!-- 推广配置标签页 -->
                <el-tab-pane label="推广配置" name="config">
                    <el-form :model="previewForm" label-width="120px">
                        <el-form-item label="广告名称">
                            <el-input v-model="currentAd.adgroup_name" disabled />
                        </el-form-item>
                        <el-form-item label="用户ID类型">
                            <el-select v-model="previewForm.user_id_type" placeholder="请选择用户ID类型">
                                <el-option label="微信ID" value="USER_ID_TYPE_WECHAT_ID" />
                                <el-option label="QQ ID" value="USER_ID_TYPE_QQ_ID" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="用户ID列表">
                            <el-input v-model="previewForm.user_id_list_text" type="textarea" :rows="4"
                                placeholder="请输入用户ID，多个ID用逗号、空格或换行分隔" @input="handleUserIdListInput" />
                            <div class="form-tip">支持逗号、空格或换行分隔，最多50个用户ID</div>
                            <div class="user-count" v-if="previewForm.user_id_list.length > 0">
                                已输入 {{ previewForm.user_id_list.length }} 个用户ID
                            </div>
                        </el-form-item>
                    </el-form>

                    <div class="dialog-actions">
                        <el-button @click="previewDialogVisible = false">取消</el-button>
                        <el-button type="primary" @click="createPreview" :loading="previewLoading">
                            开始推广
                        </el-button>
                    </div>
                </el-tab-pane>

                <!-- 推广日志标签页 -->
                <el-tab-pane label="推广日志" name="logs">
                    <div class="logs-header">
                        <el-button type="primary" size="small" @click="copyAllUserIds"
                            :disabled="previewLogs.data.length === 0">
                            <el-icon>
                                <DocumentCopy />
                            </el-icon>
                            一键复制所有微信号
                        </el-button>
                        <span class="logs-total">共 {{ previewLogs.pagination.total }} 条记录</span>
                    </div>

                    <div class="logs-list" v-loading="logsLoading">
                        <div class="log-item" v-for="log in previewLogs.data" :key="log.id">
                            <div class="log-header">
                                <span class="log-time">{{ formatTime(log.create_time) }}</span>
                                <span class="log-status" :class="log.error_message ? 'error' : 'success'">
                                    {{ log.error_message ? '失败' : `成功: ${log.success_count} 失败: ${log.fail_count}` }}
                                </span>
                            </div>
                            <div class="log-content">
                                <div class="user-list">
                                    <span class="label">推广用户:</span>
                                    <div class="user-actions">
                                        <el-button size="mini" @click="copyLogUserIds(log)">
                                            复制本组微信号
                                        </el-button>
                                    </div>
                                    <div class="user-tags">
                                        <el-tag v-for="userId in log.user_id_list" :key="userId" size="small"
                                            class="user-tag" @click="copyUserId(userId)">
                                            {{ userId }}
                                            <el-icon class="copy-icon">
                                                <DocumentCopy />
                                            </el-icon>
                                        </el-tag>
                                    </div>
                                </div>
                                <div v-if="log.wx_fail_user_name_list && log.wx_fail_user_name_list.length > 0"
                                    class="failed-users">
                                    <span class="label">失败用户:</span>
                                    <div class="user-tags">
                                        <el-tag v-for="userId in log.wx_fail_user_name_list" :key="userId" type="danger"
                                            size="small" class="user-tag" @click="copyUserId(userId)">
                                            {{ userId }}
                                            <el-icon class="copy-icon">
                                                <DocumentCopy />
                                            </el-icon>
                                        </el-tag>
                                    </div>
                                </div>
                                <div v-if="log.error_message" class="error-message">
                                    {{ log.error_message }}
                                </div>
                            </div>
                        </div>

                        <!-- 空状态 -->
                        <div class="empty-logs" v-if="previewLogs.data.length === 0 && !logsLoading">
                            <el-empty description="暂无推广记录" />
                        </div>
                    </div>

                    <!-- 推广日志分页 -->
                    <div class="pagination-container" v-if="previewLogs.pagination.total > 0">
                        <el-pagination v-model:current-page="previewLogs.pagination.page"
                            v-model:page-size="previewLogs.pagination.pageSize" :total="previewLogs.pagination.total"
                            :page-sizes="[5, 10, 20]" layout="total, sizes, prev, pager, next"
                            @size-change="fetchPreviewLogs" @current-change="fetchPreviewLogs" />
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-dialog>

        <!-- 广告管理弹窗 -->
        <el-dialog v-model="adDialogVisible" title="广告管理" width="90%" top="5vh" :close-on-click-modal="false">


            <!-- 在广告管理弹窗的 el-tabs 中添加创意管理标签页 -->
            <el-tabs v-model="activeTab" type="border-card">
                <!-- 现有的广告列表标签页 -->
                <el-tab-pane label="广告列表" name="ads">

                    <div class="ad-dialog-header">
                        <div class="ad-account-info">
                            <h3>{{ currentAdvertiser.corporation_name }}</h3>
                            <div class="account-details">
                                <span>账户ID: {{ currentAdvertiser.account_id }}</span>
                                <span>授权账号: {{ currentAdvertiser.auth_name }}</span>
                            </div>
                        </div>
                        <div class="ad-sync-section">
                            <el-button type="primary" size="large" @click="syncAccountAds" :loading="adSyncLoading">
                                <el-icon>
                                    <Refresh />
                                </el-icon>
                                同步所有广告
                            </el-button>

                            <div class="ad-sync-status" v-if="adSyncStatus.last_sync">
                                <div class="status-item">
                                    <span class="label">最后同步：</span>
                                    <span class="value">{{ formatTime(adSyncStatus.last_sync.sync_time) }}</span>
                                </div>
                                <div class="status-item">
                                    <span class="label">同步结果：</span>
                                    <span class="value success" v-if="adSyncStatus.last_sync.success_count > 0">
                                        成功 {{ adSyncStatus.last_sync.success_count }} 条
                                    </span>
                                    <span class="value fail" v-if="adSyncStatus.last_sync.fail_count > 0">
                                        失败 {{ adSyncStatus.last_sync.fail_count }} 条
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 广告统计信息 -->
                    <div class="ad-stats-section">
                        <el-row :gutter="20">
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon total">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ adSyncStatus.total_ads || 0 }}</div>
                                        <div class="stat-label">总广告数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon active">
                                        <el-icon>
                                            <CircleCheck />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ adStatusCount.active || 0 }}</div>
                                        <div class="stat-label">活跃广告</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon suspended">
                                        <el-icon>
                                            <VideoPause />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ adStatusCount.suspended || 0 }}</div>
                                        <div class="stat-label">暂停广告</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon pending">
                                        <el-icon>
                                            <Clock />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ adStatusCount.pending || 0 }}</div>
                                        <div class="stat-label">待审核</div>
                                    </div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>
                    <!-- 广告搜索区域 -->
                    <div class="ad-search-bar">
                        <el-input v-model="adSearchForm.keyword" placeholder="搜索广告名称" clearable size="large"
                            style="width: 300px; margin-right: 15px" @keyup.enter="handleAdSearch">
                            <template #prefix>
                                <el-icon>
                                    <Search />
                                </el-icon>
                            </template>
                        </el-input>

                        <el-select v-model="adSearchForm.system_status" placeholder="状态筛选" clearable size="large"
                            style="width: 150px; margin-right: 15px">
                            <el-option label="全部状态" value=""></el-option>
                            <el-option label="活跃" value="ADGROUP_STATUS_ACTIVE"></el-option>
                            <el-option label="暂停" value="ADGROUP_STATUS_SUSPEND"></el-option>
                            <el-option label="待审核" value="ADGROUP_STATUS_PENDING"></el-option>
                        </el-select>

                        <el-button type="primary" size="large" @click="handleAdSearch">
                            <el-icon>
                                <Search />
                            </el-icon>
                            搜索
                        </el-button>

                        <el-button type="info" size="large" @click="resetAdSearch">
                            <el-icon>
                                <RefreshLeft />
                            </el-icon>
                            重置
                        </el-button>
                    </div>

                    <!-- 广告列表 -->
                    <el-table :data="adList" stripe style="width: 100%; margin-top: 20px" v-loading="adLoading"
                        max-height="500">
                        <el-table-column prop="adgroup_id" label="广告ID" width="120" />
                        <el-table-column prop="adgroup_name" label="广告名称" min-width="200" show-overflow-tooltip />

                        <el-table-column label="广告状态" width="100">
                            <template #default="{ row }">
                                <el-tag :type="getAdStatusType(row.system_status)" size="small">
                                    {{ getAdStatusText(row.system_status) }}
                                </el-tag>
                            </template>
                        </el-table-column>

                        <el-table-column label="配置状态" width="100">
                            <template #default="{ row }">
                                <el-tag :type="getConfigStatusType(row.configured_status)" size="small">
                                    {{ getConfigStatusText(row.configured_status) }}
                                </el-tag>
                            </template>
                        </el-table-column>

                        <el-table-column label="日预算" width="120">
                            <template #default="{ row }">
                                <span v-if="row.daily_budget > 0">
                                    {{ formatBudget(row.daily_budget) }}
                                </span>
                                <span v-else class="empty-value">未设置</span>
                            </template>
                        </el-table-column>

                        <el-table-column prop="bid_amount" label="出价" width="100">
                            <template #default="{ row }">
                                <span v-if="row.bid_amount">¥{{ row.bid_amount }}</span>
                                <span v-else class="empty-value">未设置</span>
                            </template>
                        </el-table-column>

                        <el-table-column prop="optimization_goal" label="优化目标" width="150" show-overflow-tooltip>
                            <template #default="{ row }">
                                <span v-if="row.optimization_goal">{{ getOptimizationGoalText(row.optimization_goal)
                                }}</span>
                                <span v-else class="empty-value">未设置</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="投放站点" width="150">
                            <template #default="{ row }">
                                <span v-if="row.site_set && row.site_set.length > 0">
                                    {{ getSiteSetText(row.site_set) }}
                                </span>
                                <span v-else class="empty-value">未设置</span>
                            </template>
                        </el-table-column>

                        <el-table-column prop="created_time" label="创建时间" width="180">
                            <template #default="{ row }">
                                {{ formatTimestamp(row.created_time) }}
                            </template>
                        </el-table-column>

                        <el-table-column prop="last_sync_time" label="最后同步" width="180">
                            <template #default="{ row }">
                                {{ formatTime(row.last_sync_time) }}
                            </template>
                        </el-table-column>

                        <el-table-column label="操作" width="200" fixed="right">
                            <template #default="scope">
                                <el-button type="warning" size="small" @click="openPreviewDialog(scope.row)">
                                    <el-icon>
                                        <Promotion />
                                    </el-icon>
                                    定向推广
                                </el-button>
                                <el-button type="danger" size="small" @click="handleDeleteAd(scope.row.id)"
                                    :loading="deleteAdLoading[scope.row.id]">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                    删除
                                </el-button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <!-- 广告分页组件 -->
                    <div class="pagination-container">
                        <el-pagination v-model:current-page="adPagination.page"
                            v-model:page-size="adPagination.pageSize" :total="adPagination.total"
                            :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                            @size-change="fetchAdList" @current-change="fetchAdList" />
                    </div>
                </el-tab-pane>

                <!-- 新增创意管理标签页 -->
                <el-tab-pane label="创意管理" name="creatives">
                    <div class="creative-management">
                        <!-- 创意同步区域 -->
                        <div class="creative-sync-section">
                            <el-button type="primary" size="large" @click="syncAccountCreatives"
                                :loading="creativeSyncLoading">
                                <el-icon>
                                    <Refresh />
                                </el-icon>
                                同步所有创意
                            </el-button>

                            <div class="creative-sync-status" v-if="creativeSyncStatus.last_sync">
                                <div class="status-item">
                                    <span class="label">最后同步：</span>
                                    <span class="value">{{ formatTime(creativeSyncStatus.last_sync.sync_time) }}</span>
                                </div>
                                <div class="status-item">
                                    <span class="label">同步结果：</span>
                                    <span class="value success" v-if="creativeSyncStatus.last_sync.success_count > 0">
                                        成功 {{ creativeSyncStatus.last_sync.success_count }} 条
                                    </span>
                                    <span class="value fail" v-if="creativeSyncStatus.last_sync.fail_count > 0">
                                        失败 {{ creativeSyncStatus.last_sync.fail_count }} 条
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- 创意统计信息 -->
                        <div class="creative-stats-section">
                            <el-row :gutter="20">
                                <el-col :span="6">
                                    <div class="stat-card">
                                        <div class="stat-icon total">
                                            <el-icon>
                                                <Document />
                                            </el-icon>
                                        </div>
                                        <div class="stat-content">
                                            <div class="stat-number">{{ creativeSyncStatus.total_creatives || 0 }}</div>
                                            <div class="stat-label">总创意数</div>
                                        </div>
                                    </div>
                                </el-col>
                                <el-col :span="6">
                                    <div class="stat-card">
                                        <div class="stat-icon active">
                                            <el-icon>
                                                <CircleCheck />
                                            </el-icon>
                                        </div>
                                        <div class="stat-content">
                                            <div class="stat-number">{{ creativeStatusCount.active || 0 }}</div>
                                            <div class="stat-label">活跃创意</div>
                                        </div>
                                    </div>
                                </el-col>
                                <el-col :span="6">
                                    <div class="stat-card">
                                        <div class="stat-icon suspended">
                                            <el-icon>
                                                <VideoPause />
                                            </el-icon>
                                        </div>
                                        <div class="stat-content">
                                            <div class="stat-number">{{ creativeStatusCount.suspended || 0 }}</div>
                                            <div class="stat-label">暂停创意</div>
                                        </div>
                                    </div>
                                </el-col>
                                <el-col :span="6">
                                    <div class="stat-card">
                                        <div class="stat-icon pending">
                                            <el-icon>
                                                <Clock />
                                            </el-icon>
                                        </div>
                                        <div class="stat-content">
                                            <div class="stat-number">{{ creativeStatusCount.pending || 0 }}</div>
                                            <div class="stat-label">待审核</div>
                                        </div>
                                    </div>
                                </el-col>
                            </el-row>
                        </div>

                        <!-- 创意搜索区域 -->
                        <div class="creative-search-bar">
                            <el-input v-model="creativeSearchForm.keyword" placeholder="搜索创意名称、广告名称" clearable
                                size="large" style="width: 300px; margin-right: 15px"
                                @keyup.enter="handleCreativeSearch">
                                <template #prefix>
                                    <el-icon>
                                        <Search />
                                    </el-icon>
                                </template>
                            </el-input>

                            <el-select v-model="creativeSearchForm.system_status" placeholder="状态筛选" clearable
                                size="large" style="width: 150px; margin-right: 15px">
                                <el-option label="全部状态" value=""></el-option>
                                <el-option label="活跃" value="DYNAMIC_CREATIVE_STATUS_ACTIVE"></el-option>
                                <el-option label="暂停" value="DYNAMIC_CREATIVE_STATUS_SUSPEND"></el-option>
                                <el-option label="待审核" value="DYNAMIC_CREATIVE_STATUS_PENDING"></el-option>
                            </el-select>

                            <el-button type="primary" size="large" @click="handleCreativeSearch">
                                <el-icon>
                                    <Search />
                                </el-icon>
                                搜索
                            </el-button>

                            <el-button type="info" size="large" @click="resetCreativeSearch">
                                <el-icon>
                                    <RefreshLeft />
                                </el-icon>
                                重置
                            </el-button>
                        </div>

                        <!-- 创意列表 -->
                        <el-table :data="creativeList" stripe style="width: 100%; margin-top: 20px"
                            v-loading="creativeLoading" max-height="500">
                            <el-table-column prop="dynamic_creative_id" label="创意ID" width="120" />
                            <el-table-column prop="dynamic_creative_name" label="创意名称" min-width="200"
                                show-overflow-tooltip />
                            <el-table-column prop="adgroup_id" label="广告ID" width="120" />
                            <el-table-column prop="adgroup_name" label="广告名称" width="150" show-overflow-tooltip />

                            <el-table-column label="创意状态" width="100">
                                <template #default="{ row }">
                                    <el-tag :type="getCreativeStatusType(row.system_status)" size="small">
                                        {{ getCreativeStatusText(row.system_status) }}
                                    </el-tag>
                                </template>
                            </el-table-column>

                            <el-table-column label="配置状态" width="100">
                                <template #default="{ row }">
                                    <el-tag :type="getConfigStatusType(row.configured_status)" size="small">
                                        {{ getConfigStatusText(row.configured_status) }}
                                    </el-tag>
                                </template>
                            </el-table-column>

                            <el-table-column prop="corporation_name" label="广告主" width="150" show-overflow-tooltip />

                            <el-table-column prop="last_sync_time" label="最后同步" width="180">
                                <template #default="{ row }">
                                    {{ formatTime(row.last_sync_time) }}
                                </template>
                            </el-table-column>

                            <el-table-column label="操作" width="200" fixed="right">
                                <template #default="scope">
                                    <el-button type="warning" size="small"
                                        @click="openCreativePreviewDialog(scope.row)">
                                        <el-icon>
                                            <Promotion />
                                        </el-icon>
                                        创意预览
                                    </el-button>
                                    <el-button type="danger" size="small" @click="handleDeleteCreative(scope.row.id)"
                                        :loading="deleteCreativeLoading[scope.row.id]">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                </template>
                            </el-table-column>
                        </el-table>

                        <!-- 创意分页组件 -->
                        <div class="pagination-container">
                            <el-pagination v-model:current-page="creativePagination.page"
                                v-model:page-size="creativePagination.pageSize" :total="creativePagination.total"
                                :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                                @size-change="fetchCreativeList" @current-change="fetchCreativeList" />
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-dialog>

        <!-- 创意预览弹窗 -->
        <el-dialog v-model="creativePreviewDialogVisible" title="创意预览" width="800px" :close-on-click-modal="false">
            <el-tabs v-model="creativePreviewActiveTab" type="border-card">
                <!-- 预览配置标签页 -->
                <el-tab-pane label="预览配置" name="config">
                    <el-form :model="creativePreviewForm" label-width="120px">
                        <el-form-item label="创意名称">
                            <el-input v-model="currentCreative.dynamic_creative_name" disabled />
                        </el-form-item>
                        <el-form-item label="创意ID">
                            <el-input v-model="currentCreative.dynamic_creative_id" disabled />
                        </el-form-item>
                        <el-form-item label="用户ID列表">
                            <el-input v-model="creativePreviewForm.user_id_list_text" type="textarea" :rows="4"
                                placeholder="请输入用户ID，多个ID用逗号、空格或换行分隔" @input="handleCreativeUserIdListInput" />
                            <div class="form-tip">支持逗号、空格或换行分隔，最多50个用户ID</div>
                            <div class="user-count" v-if="creativePreviewForm.user_id_list.length > 0">
                                已输入 {{ creativePreviewForm.user_id_list.length }} 个用户ID
                            </div>
                        </el-form-item>
                    </el-form>

                    <div class="dialog-actions">
                        <el-button @click="creativePreviewDialogVisible = false">取消</el-button>
                        <el-button type="primary" @click="createCreativePreview" :loading="creativePreviewLoading">
                            开始预览
                        </el-button>
                    </div>
                </el-tab-pane>

                <!-- 预览日志标签页 -->
                <el-tab-pane label="预览日志" name="logs">
                    <div class="logs-header">
                        <el-button type="primary" size="small" @click="copyAllCreativeUserIds"
                            :disabled="creativePreviewLogs.data.length === 0">
                            <el-icon>
                                <DocumentCopy />
                            </el-icon>
                            一键复制所有微信号
                        </el-button>
                        <span class="logs-total">共 {{ creativePreviewLogs.pagination.total }} 条记录</span>
                    </div>

                    <div class="logs-list" v-loading="creativeLogsLoading">
                        <div class="log-item" v-for="log in creativePreviewLogs.data" :key="log.id">
                            <div class="log-header">
                                <span class="log-time">{{ formatTime(log.create_time) }}</span>
                                <span class="log-status" :class="log.error_message ? 'error' : 'success'">
                                    {{ log.error_message ? '失败' : `成功: ${log.success_count} 失败: ${log.fail_count}` }}
                                </span>
                            </div>
                            <div class="log-content">
                                <div class="user-list">
                                    <span class="label">预览用户:</span>
                                    <div class="user-actions">
                                        <el-button size="mini" @click="copyCreativeLogUserIds(log)">
                                            复制本组微信号
                                        </el-button>
                                    </div>
                                    <div class="user-tags">
                                        <el-tag v-for="userId in log.user_id_list" :key="userId" size="small"
                                            class="user-tag" @click="copyUserId(userId)">
                                            {{ userId }}
                                            <el-icon class="copy-icon">
                                                <DocumentCopy />
                                            </el-icon>
                                        </el-tag>
                                    </div>
                                </div>
                                <div v-if="log.wx_fail_user_name_list && log.wx_fail_user_name_list.length > 0"
                                    class="failed-users">
                                    <span class="label">失败用户:</span>
                                    <div class="user-tags">
                                        <el-tag v-for="userId in log.wx_fail_user_name_list" :key="userId" type="danger"
                                            size="small" class="user-tag" @click="copyUserId(userId)">
                                            {{ userId }}
                                            <el-icon class="copy-icon">
                                                <DocumentCopy />
                                            </el-icon>
                                        </el-tag>
                                    </div>
                                </div>
                                <div v-if="log.error_message" class="error-message">
                                    {{ log.error_message }}
                                </div>
                            </div>
                        </div>

                        <!-- 空状态 -->
                        <div class="empty-logs" v-if="creativePreviewLogs.data.length === 0 && !creativeLogsLoading">
                            <el-empty description="暂无预览记录" />
                        </div>
                    </div>

                    <!-- 预览日志分页 -->
                    <div class="pagination-container" v-if="creativePreviewLogs.pagination.total > 0">
                        <el-pagination v-model:current-page="creativePreviewLogs.pagination.page"
                            v-model:page-size="creativePreviewLogs.pagination.pageSize"
                            :total="creativePreviewLogs.pagination.total" :page-sizes="[5, 10, 20]"
                            layout="total, sizes, prev, pager, next" @size-change="fetchCreativePreviewLogs"
                            @current-change="fetchCreativePreviewLogs" />
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Delete, Refresh, Document, View,
    CircleCheck, Clock, CloseBold, RefreshLeft, VideoPause, DocumentCopy
} from '@element-plus/icons-vue'

export default {
    name: 'AdvertiserManagement',
    components: {
        Search, Delete, Refresh, Document, View,
        CircleCheck, Clock, CloseBold, RefreshLeft, VideoPause, DocumentCopy
    },
    data() {
        return {
            loading: false,
            syncLoading: false,
            deleteLoading: {},
            hasAuthRecords: false,
            searchForm: {
                keyword: '',
                system_status: ''
            },
            previewActiveTab: 'config', // 定向推广弹窗的当前标签页
            logsLoading: false, // 日志加载状态
            advertiserList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            syncStatus: {
                last_sync: null,
                total_advertisers: 0
            },
            statusCount: {
                normal: 0,
                pending: 0,
                rejected: 0
            },
            // 定向推广相关数据
            previewDialogVisible: false,
            previewLoading: false,
            currentAd: {},
            previewForm: {
                user_id_type: 'USER_ID_TYPE_WECHAT_ID',
                user_id_list_text: '',
                user_id_list: []
            },
            previewLogs: {
                data: [],
                pagination: {
                    page: 1,
                    pageSize: 5,
                    total: 0
                }
            },
            // 广告管理相关数据
            adDialogVisible: false,
            currentAdvertiser: {},
            adLoading: false,
            adSyncLoading: false,
            deleteAdLoading: {},
            adSearchForm: {
                keyword: '',
                system_status: ''
            },
            adList: [],
            adPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            adSyncStatus: {
                last_sync: null,
                total_ads: 0
            },
            adStatusCount: {
                active: 0,
                suspended: 0,
                pending: 0
            },

            // 新增创意管理相关数据
            activeTab: 'ads', // 当前激活的标签页
            creativeSyncLoading: false,
            creativeLoading: false,
            deleteCreativeLoading: {},
            creativeSearchForm: {
                keyword: '',
                system_status: ''
            },
            creativeList: [],
            creativePagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            creativeSyncStatus: {
                last_sync: null,
                total_creatives: 0
            },
            creativeStatusCount: {
                active: 0,
                suspended: 0,
                pending: 0
            },

            // 创意预览相关数据
            creativePreviewDialogVisible: false,
            creativePreviewLoading: false,
            creativePreviewActiveTab: 'config',
            creativeLogsLoading: false,
            currentCreative: {},
            creativePreviewForm: {
                user_id_list_text: '',
                user_id_list: []
            },
            creativePreviewLogs: {
                data: [],
                pagination: {
                    page: 1,
                    pageSize: 5,
                    total: 0
                }
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.checkAuthRecords();
        this.fetchAdvertiserList();
        this.fetchSyncStatus();
    },
    methods: {

        // 同步账户创意数据
        async syncAccountCreatives() {
            if (!this.currentAdvertiser.ghz_appid) {
                this.$message.error('缺少授权令牌，无法同步创意数据');
                return;
            }

            this.$confirm('确定要同步该账户的所有创意数据吗？', '同步确认', {
                confirmButtonText: '开始同步',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.creativeSyncLoading = true;

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/creative_sync.php`,
                        {
                            action: 'sync_account_creatives',
                            store_id: this.userInfo.id,
                            account_id: this.currentAdvertiser.account_id,
                            auth_id: this.currentAdvertiser.auth_id,
                            access_token: this.currentAdvertiser.ghz_appid,
                            domain: window.location.hostname
                        }
                    );

                    if (response.data.success) {
                        this.$message.success(response.data.message);
                        this.fetchCreativeList();
                        this.fetchCreativeSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '创意同步失败');
                    }
                } catch (error) {
                    let errorMsg = '创意同步失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('创意同步错误:', error);
                } finally {
                    this.creativeSyncLoading = false;
                }
            }).catch(() => {
                // 用户取消
            });
        },

        // 获取创意列表 - 添加 account_id 参数
        async fetchCreativeList() {
            this.creativeLoading = true;
            try {
                const params = {
                    action: this.creativeSearchForm.keyword ? 'search_creatives' : 'list_creatives',
                    store_id: this.userInfo.id,
                    account_id: this.currentAdvertiser.account_id, // 关键：添加账户ID
                    page: this.creativePagination.page,
                    page_size: this.creativePagination.pageSize
                };

                if (this.creativeSearchForm.keyword) {
                    params.keyword = this.creativeSearchForm.keyword;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/creative_sync.php`,
                    params
                );

                if (data.data) {
                    this.creativeList = data.data;
                    this.creativePagination.total = data.pagination?.total || data.data.length;
                    this.calculateCreativeStatusCount();
                }
            } catch (error) {
                this.$message.error('获取创意列表失败');
                console.error('获取创意列表错误:', error);
            } finally {
                this.creativeLoading = false;
            }
        },

        // 获取创意同步状态 - 添加 account_id
        async fetchCreativeSyncStatus() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/creative_sync.php`,
                    {
                        action: 'creative_sync_status',
                        store_id: this.userInfo.id,
                        account_id: this.currentAdvertiser.account_id // 关键：添加账户ID
                    }
                );

                if (response.data) {
                    this.creativeSyncStatus = response.data;
                }
            } catch (error) {
                console.error('获取创意同步状态失败:', error);
            }
        },

        // 计算创意状态统计
        calculateCreativeStatusCount() {
            this.creativeStatusCount = {
                active: 0,
                suspended: 0,
                pending: 0
            };

            this.creativeList.forEach(item => {
                switch (item.system_status) {
                    case 'DYNAMIC_CREATIVE_STATUS_ACTIVE':
                        this.creativeStatusCount.active++;
                        break;
                    case 'DYNAMIC_CREATIVE_STATUS_SUSPEND':
                        this.creativeStatusCount.suspended++;
                        break;
                    case 'DYNAMIC_CREATIVE_STATUS_PENDING':
                        this.creativeStatusCount.pending++;
                        break;
                }
            });
        },

        // 处理创意搜索
        handleCreativeSearch() {
            this.creativePagination.page = 1;
            this.fetchCreativeList();
        },

        // 重置创意搜索
        resetCreativeSearch() {
            this.creativeSearchForm = {
                keyword: '',
                system_status: ''
            };
            this.creativePagination.page = 1;
            this.fetchCreativeList();
        },

        // 删除创意
        async handleDeleteCreative(id) {
            this.$confirm('确定要删除该创意数据吗？删除后需要重新同步才能恢复。', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.deleteCreativeLoading = { ...this.deleteCreativeLoading, [id]: true };

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/creative_sync.php`,
                        {
                            action: 'delete_creative',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (response.data.success) {
                        this.$message.success('删除成功');
                        this.fetchCreativeList();
                        this.fetchCreativeSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '删除失败');
                    }
                } catch (error) {
                    let errorMsg = '删除失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('删除创意错误:', error);
                } finally {
                    this.deleteCreativeLoading = { ...this.deleteCreativeLoading, [id]: false };
                }
            }).catch(() => { });
        },

        // 打开创意预览弹窗
        openCreativePreviewDialog(row) {
            this.currentCreative = { ...row };
            this.creativePreviewForm = {
                user_id_list_text: '',
                user_id_list: []
            };
            this.creativePreviewActiveTab = 'config';
            this.creativePreviewDialogVisible = true;
            this.fetchCreativePreviewLogs();
        },

        // 处理创意用户ID列表输入
        handleCreativeUserIdListInput() {
            const text = this.creativePreviewForm.user_id_list_text.trim();

            if (!text) {
                this.creativePreviewForm.user_id_list = [];
                return;
            }

            this.creativePreviewForm.user_id_list = text
                .split(/[,，\s\n]+/)
                .map(id => id.trim())
                .filter(id => id.length > 0)
                .slice(0, 50);
        },

        // 创建创意预览
        async createCreativePreview() {
            if (this.creativePreviewForm.user_id_list.length === 0) {
                this.$message.warning('请输入至少一个用户ID');
                return;
            }

            if (this.creativePreviewForm.user_id_list.length > 50) {
                this.$message.warning('最多支持50个用户ID');
                return;
            }

            this.creativePreviewLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/creative_sync.php`,
                    {
                        action: 'create_creative_preview',
                        store_id: this.userInfo.id,
                        auth_id: this.currentAdvertiser.auth_id,
                        account_id: this.currentAdvertiser.account_id,
                        dynamic_creative_id: this.currentCreative.dynamic_creative_id,
                        access_token: this.currentAdvertiser.ghz_appid,
                        domain: window.location.hostname,
                        preview_creative_components: {},
                        user_id_list: this.creativePreviewForm.user_id_list
                    }
                );

                if (response.data.success) {
                    this.$message.success(response.data.message);
                    this.creativePreviewActiveTab = 'logs';
                    this.fetchCreativePreviewLogs();
                } else {
                    this.$message.error(response.data.error || '预览失败');
                }
            } catch (error) {
                let errorMsg = '预览失败';
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                }
                this.$message.error(errorMsg);
                console.error('创意预览错误:', error);
            } finally {
                this.creativePreviewLoading = false;
            }
        },

        // 获取创意预览日志
        async fetchCreativePreviewLogs() {
            this.creativeLogsLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/creative_sync.php`,
                    {
                        action: 'get_creative_preview_logs',
                        store_id: this.userInfo.id,
                        page: this.creativePreviewLogs.pagination.page,
                        page_size: this.creativePreviewLogs.pagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.creativePreviewLogs.data = response.data.data;
                    this.creativePreviewLogs.pagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                console.error('获取创意预览日志失败:', error);
                this.$message.error('获取创意预览日志失败');
            } finally {
                this.creativeLogsLoading = false;
            }
        },

        // 一键复制所有创意预览微信号
        async copyAllCreativeUserIds() {
            const allUserIds = [];

            this.creativePreviewLogs.data.forEach(log => {
                if (log.user_id_list && log.user_id_list.length > 0) {
                    allUserIds.push(...log.user_id_list);
                }
            });

            if (allUserIds.length === 0) {
                this.$message.warning('没有可复制的用户ID');
                return;
            }

            const uniqueUserIds = [...new Set(allUserIds)];
            const text = uniqueUserIds.join('\n');

            await this.copyToClipboard(text);
            this.$message.success(`已复制 ${uniqueUserIds.length} 个用户ID`);
        },

        // 复制创意日志用户ID
        async copyCreativeLogUserIds(log) {
            if (!log.user_id_list || log.user_id_list.length === 0) {
                this.$message.warning('该记录没有用户ID');
                return;
            }

            const text = log.user_id_list.join('\n');
            await this.copyToClipboard(text);
            this.$message.success(`已复制 ${log.user_id_list.length} 个用户ID`);
        },

        // 创意状态相关方法
        getCreativeStatusType(status) {
            const typeMap = {
                'DYNAMIC_CREATIVE_STATUS_ACTIVE': 'success',
                'DYNAMIC_CREATIVE_STATUS_SUSPEND': 'warning',
                'DYNAMIC_CREATIVE_STATUS_PENDING': 'info'
            };
            return typeMap[status] || 'info';
        },

        getCreativeStatusText(status) {
            const textMap = {
                'DYNAMIC_CREATIVE_STATUS_ACTIVE': '活跃',
                'DYNAMIC_CREATIVE_STATUS_SUSPEND': '暂停',
                'DYNAMIC_CREATIVE_STATUS_PENDING': '待审核'
            };
            return textMap[status] || status;
        },
        // 检查是否有授权记录
        async checkAuthRecords() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
                    {
                        action: 'list_all',
                        store_id: this.userInfo.id
                    }
                );

                if (response.data.data && response.data.data.length > 0) {
                    const hasValidAuth = response.data.data.some(item =>
                        item.ghz_appid && item.ghz_appid.trim() !== ''
                    );
                    this.hasAuthRecords = hasValidAuth;
                }
            } catch (error) {
                console.error('检查授权记录失败:', error);
                this.hasAuthRecords = false;
            }
        },
        // 打开定向推广弹窗
        openPreviewDialog(row) {
            this.currentAd = { ...row };
            this.previewForm = {
                user_id_type: 'USER_ID_TYPE_WECHAT_ID',
                user_id_list_text: '',
                user_id_list: []
            };
            this.previewActiveTab = 'config'; // 默认显示配置标签页
            this.previewDialogVisible = true;
            this.fetchPreviewLogs();
        },

        // 一键复制所有微信号
        async copyAllUserIds() {
            const allUserIds = [];

            this.previewLogs.data.forEach(log => {
                if (log.user_id_list && log.user_id_list.length > 0) {
                    allUserIds.push(...log.user_id_list);
                }
            });

            if (allUserIds.length === 0) {
                this.$message.warning('没有可复制的用户ID');
                return;
            }

            // 去重
            const uniqueUserIds = [...new Set(allUserIds)];
            const text = uniqueUserIds.join('\n');

            await this.copyToClipboard(text);
            this.$message.success(`已复制 ${uniqueUserIds.length} 个用户ID`);
        },

        // 复制单条日志的所有用户ID
        async copyLogUserIds(log) {
            if (!log.user_id_list || log.user_id_list.length === 0) {
                this.$message.warning('该记录没有用户ID');
                return;
            }

            const text = log.user_id_list.join('\n');
            await this.copyToClipboard(text);
            this.$message.success(`已复制 ${log.user_id_list.length} 个用户ID`);
        },

        // 复制单个用户ID
        async copyUserId(userId) {
            await this.copyToClipboard(userId);
            this.$message.success('已复制: ' + userId);
        },

        // 通用的复制到剪贴板方法
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
            } catch (err) {
                // 降级方案
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
        },

        // 获取推广日志
        async fetchPreviewLogs() {
            this.logsLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/ad_preview.php`,
                    {
                        action: 'get_preview_logs',
                        store_id: this.userInfo.id,
                        page: this.previewLogs.pagination.page,
                        page_size: this.previewLogs.pagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.previewLogs.data = response.data.data;
                    this.previewLogs.pagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                console.error('获取推广日志失败:', error);
                this.$message.error('获取推广日志失败');
            } finally {
                this.logsLoading = false;
            }
        },
        // 处理用户ID列表输入
        handleUserIdListInput() {
            const text = this.previewForm.user_id_list_text.trim();

            if (!text) {
                this.previewForm.user_id_list = [];
                return;
            }

            // 使用更严格的分隔符处理
            this.previewForm.user_id_list = text
                .split(/[,，\s\n]+/)  // 支持逗号、中文逗号、空格、换行
                .map(id => id.trim())  // 去除每个ID的前后空格
                .filter(id => {
                    // 过滤空字符串和无效ID
                    if (!id) return false;
                    // 可以根据需要添加更多的验证逻辑
                    return id.length > 0;
                })
                .slice(0, 50); // 限制最多50个

            console.log('解析后的用户ID列表:', this.previewForm.user_id_list); // 调试用
        },
        // 创建定向推广
        async createPreview() {
            if (this.previewForm.user_id_list.length === 0) {
                this.$message.warning('请输入至少一个用户ID');
                return;
            }

            if (this.previewForm.user_id_list.length > 50) {
                this.$message.warning('最多支持50个用户ID');
                return;
            }

            this.previewLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/ad_preview.php`,
                    {
                        action: 'create_preview',
                        store_id: this.userInfo.id,
                        auth_id: this.currentAdvertiser.auth_id,
                        account_id: this.currentAdvertiser.account_id,
                        adgroup_id: this.currentAd.adgroup_id,
                        access_token: this.currentAdvertiser.ghz_appid,
                        domain: window.location.hostname,
                        user_id_type: this.previewForm.user_id_type,
                        user_id_list: this.previewForm.user_id_list
                    }
                );

                if (response.data.success) {
                    this.$message.success(response.data.message);
                    this.previewActiveTab = 'logs'; // 切换到日志标签页
                    this.fetchPreviewLogs(); // 刷新日志

                } else {
                    this.$message.error(response.data.error || '推广失败');
                }
            } catch (error) {
                let errorMsg = '推广失败';
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                }
                this.$message.error(errorMsg);
                console.error('推广错误:', error);
            } finally {
                this.previewLoading = false;
            }
        },


        // 同步所有广告商数据
        async syncAllAdvertisers() {
            if (!this.hasAuthRecords) {
                this.$message.warning('没有可用的授权记录，请先添加TX广告授权账号');
                return;
            }

            this.$confirm('确定要同步所有广告商数据吗？这将更新现有的数据并添加新的广告商。', '同步确认', {
                confirmButtonText: '开始同步',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.syncLoading = true;

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/advertiser_sync.php`,
                        {
                            action: 'sync_all',
                            store_id: this.userInfo.id,
                            domain: window.location.hostname
                        }
                    );

                    if (response.data.success) {
                        this.$message.success(response.data.message);
                        this.fetchAdvertiserList();
                        this.fetchSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '同步失败');

                        // 显示详细错误信息
                        if (response.data.errors) {
                            response.data.errors.forEach(error => {
                                this.$message.error(error);
                            });
                        }
                    }
                } catch (error) {
                    let errorMsg = '同步失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('同步错误:', error);
                } finally {
                    this.syncLoading = false;
                }
            }).catch(() => {
                // 用户取消
            });
        },

        // 获取广告商列表
        async fetchAdvertiserList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.keyword ? 'search' : 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                if (this.searchForm.keyword) {
                    params.keyword = this.searchForm.keyword;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/advertiser_sync.php`,
                    params
                );

                if (data.data) {
                    this.advertiserList = data.data;
                    this.pagination.total = data.pagination?.total || data.data.length;
                    this.calculateStatusCount();
                }
            } catch (error) {
                this.$message.error('获取广告商列表失败');
                console.error('获取广告商列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 获取同步状态
        async fetchSyncStatus() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/advertiser_sync.php`,
                    {
                        action: 'sync_status',
                        store_id: this.userInfo.id
                    }
                );

                if (response.data) {
                    this.syncStatus = response.data;
                }
            } catch (error) {
                console.error('获取同步状态失败:', error);
            }
        },

        // 计算状态统计
        calculateStatusCount() {
            this.statusCount = {
                normal: 0,
                pending: 0,
                rejected: 0
            };

            this.advertiserList.forEach(item => {
                switch (item.system_status) {
                    case 'CUSTOMER_STATUS_NORMAL':
                        this.statusCount.normal++;
                        break;
                    case 'CUSTOMER_STATUS_PENDING':
                        this.statusCount.pending++;
                        break;
                    case 'CUSTOMER_STATUS_DENIED':
                        this.statusCount.rejected++;
                        break;
                }
            });
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchAdvertiserList();
        },

        // 重置搜索
        resetSearch() {
            this.searchForm = {
                keyword: '',
                system_status: ''
            };
            this.pagination.page = 1;
            this.fetchAdvertiserList();
        },

        // 处理排序
        handleSortChange({ prop, order }) {
            if (order) {
                this.advertiserList.sort((a, b) => {
                    if (order === 'ascending') {
                        return a[prop] > b[prop] ? 1 : -1;
                    } else {
                        return a[prop] < b[prop] ? 1 : -1;
                    }
                });
            }
        },

        // 删除广告商
        async handleDelete(id) {
            this.$confirm('确定要删除该广告商数据吗？删除后需要重新同步才能恢复。', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.deleteLoading = { ...this.deleteLoading, [id]: true };

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/advertiser_sync.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (response.data.success) {
                        this.$message.success('删除成功');
                        this.fetchAdvertiserList();
                        this.fetchSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '删除失败');
                    }
                } catch (error) {
                    let errorMsg = '删除失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('删除错误:', error);
                } finally {
                    this.deleteLoading = { ...this.deleteLoading, [id]: false };
                }
            }).catch(() => { });
        },

        // 打开广告管理弹窗
        async openAdDialog(row) {
            // 首先获取完整的授权信息，包含 ghz_appid
            try {
                const authResponse = await axios.post(
                    `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
                    {
                        action: 'list_all',
                        store_id: this.userInfo.id
                    }
                );

                if (authResponse.data.data) {
                    // 找到对应的授权记录
                    const authRecord = authResponse.data.data.find(
                        item => item.id === row.auth_id
                    );

                    if (authRecord && authRecord.ghz_appid) {
                        this.currentAdvertiser = {
                            ...row,
                            ghz_appid: authRecord.ghz_appid,
                            auth_name: authRecord.ghz_name
                        };
                        this.adDialogVisible = true;
                        this.activeTab = 'ads'; // 默认显示广告标签页

                        this.adSearchForm = { keyword: '', system_status: '' };
                        this.creativeSearchForm = { keyword: '', system_status: '' };
                        this.adPagination.page = 1;
                        this.creativePagination.page = 1;


                        // 获取广告数据和同步状态
                        await this.fetchAdList();
                        await this.fetchAdSyncStatus();
                        await this.fetchCreativeList(); // 新增：获取创意列表
                        await this.fetchCreativeSyncStatus(); // 新增：获取创意同步状态
                    } else {
                        this.$message.error('未找到有效的授权令牌，请检查授权配置');
                    }
                }
            } catch (error) {
                console.error('获取授权信息失败:', error);
                this.$message.error('获取授权信息失败');
            }
        },

        // 同步账户广告数据 - 确保传递所有必要参数
        async syncAccountAds() {
            // 检查必要参数
            if (!this.currentAdvertiser.ghz_appid) {
                this.$message.error('缺少授权令牌，无法同步广告数据');
                return;
            }

            this.$confirm('确定要同步该账户的所有广告数据吗？', '同步确认', {
                confirmButtonText: '开始同步',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.adSyncLoading = true;

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/ad_sync.php`,
                        {
                            action: 'sync_account_ads',
                            store_id: this.userInfo.id,
                            account_id: this.currentAdvertiser.account_id,
                            auth_id: this.currentAdvertiser.auth_id,
                            access_token: this.currentAdvertiser.ghz_appid, // 确保传递 access_token
                            domain: window.location.hostname
                        }
                    );

                    if (response.data.success) {
                        this.$message.success(response.data.message);
                        this.fetchAdList();
                        this.fetchAdSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '广告同步失败');
                    }
                } catch (error) {
                    let errorMsg = '广告同步失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('广告同步错误:', error);
                } finally {
                    this.adSyncLoading = false;
                }
            }).catch(() => {
                // 用户取消
            });
        },

        // 获取广告列表 - 添加 account_id 参数
        async fetchAdList() {
            this.adLoading = true;
            try {
                const params = {
                    action: this.adSearchForm.keyword ? 'search_ads' : 'list_ads',
                    store_id: this.userInfo.id,
                    account_id: this.currentAdvertiser.account_id, // 关键：添加账户ID
                    page: this.adPagination.page,
                    page_size: this.adPagination.pageSize
                };

                if (this.adSearchForm.keyword) {
                    params.keyword = this.adSearchForm.keyword;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/ad_sync.php`,
                    params
                );

                if (data.data) {
                    this.adList = data.data;
                    this.adPagination.total = data.pagination?.total || data.data.length;
                    this.calculateAdStatusCount();
                }
            } catch (error) {
                this.$message.error('获取广告列表失败');
                console.error('获取广告列表错误:', error);
            } finally {
                this.adLoading = false;
            }
        },

        // 获取广告同步状态 - 添加 account_id
        async fetchAdSyncStatus() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/ad_sync.php`,
                    {
                        action: 'ad_sync_status',
                        store_id: this.userInfo.id,
                        account_id: this.currentAdvertiser.account_id // 关键：添加账户ID
                    }
                );

                if (response.data) {
                    this.adSyncStatus = response.data;
                }
            } catch (error) {
                console.error('获取广告同步状态失败:', error);
            }
        },

        // 计算广告状态统计
        calculateAdStatusCount() {
            this.adStatusCount = {
                active: 0,
                suspended: 0,
                pending: 0
            };

            this.adList.forEach(item => {
                switch (item.system_status) {
                    case 'ADGROUP_STATUS_ACTIVE':
                        this.adStatusCount.active++;
                        break;
                    case 'ADGROUP_STATUS_SUSPEND':
                        this.adStatusCount.suspended++;
                        break;
                    case 'ADGROUP_STATUS_PENDING':
                        this.adStatusCount.pending++;
                        break;
                }
            });
        },

        // 处理广告搜索
        handleAdSearch() {
            this.adPagination.page = 1;
            this.fetchAdList();
        },

        // 重置广告搜索
        resetAdSearch() {
            this.adSearchForm = {
                keyword: '',
                system_status: ''
            };
            this.adPagination.page = 1;
            this.fetchAdList();
        },

        // 删除广告
        async handleDeleteAd(id) {
            this.$confirm('确定要删除该广告数据吗？删除后需要重新同步才能恢复。', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.deleteAdLoading = { ...this.deleteAdLoading, [id]: true };

                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/ad_sync.php`,
                        {
                            action: 'delete_ad',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (response.data.success) {
                        this.$message.success('删除成功');
                        this.fetchAdList();
                        this.fetchAdSyncStatus();
                    } else {
                        this.$message.error(response.data.error || '删除失败');
                    }
                } catch (error) {
                    let errorMsg = '删除失败';
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error;
                    }
                    this.$message.error(errorMsg);
                    console.error('删除广告错误:', error);
                } finally {
                    this.deleteAdLoading = { ...this.deleteAdLoading, [id]: false };
                }
            }).catch(() => { });
        },

        // 工具方法
        getStatusType(status) {
            const typeMap = {
                'CUSTOMER_STATUS_NORMAL': 'success',
                'CUSTOMER_STATUS_PENDING': 'warning',
                'CUSTOMER_STATUS_DENIED': 'danger'
            };
            return typeMap[status] || 'info';
        },

        getStatusText(status) {
            const textMap = {
                'CUSTOMER_STATUS_NORMAL': '正常',
                'CUSTOMER_STATUS_PENDING': '待审核',
                'CUSTOMER_STATUS_DENIED': '审核拒绝'
            };
            return textMap[status] || status;
        },

        getAdStatusType(status) {
            const typeMap = {
                'ADGROUP_STATUS_ACTIVE': 'success',
                'ADGROUP_STATUS_SUSPEND': 'warning',
                'ADGROUP_STATUS_PENDING': 'info'
            };
            return typeMap[status] || 'info';
        },

        getAdStatusText(status) {
            const textMap = {
                'ADGROUP_STATUS_ACTIVE': '活跃',
                'ADGROUP_STATUS_SUSPEND': '暂停',
                'ADGROUP_STATUS_PENDING': '待审核'
            };
            return textMap[status] || status;
        },

        getConfigStatusType(status) {
            const typeMap = {
                'AD_STATUS_NORMAL': 'success',
                'AD_STATUS_SUSPEND': 'warning',
                'AD_STATUS_PENDING': 'info'
            };
            return typeMap[status] || 'info';
        },

        getConfigStatusText(status) {
            const textMap = {
                'AD_STATUS_NORMAL': '正常',
                'AD_STATUS_SUSPEND': '暂停',
                'AD_STATUS_PENDING': '待审核'
            };
            return textMap[status] || status;
        },

        getOptimizationGoalText(goal) {
            const goalMap = {
                'OPTIMIZATIONGOAL_ECOMMERCE_ORDER': '电商订单',
                'OPTIMIZATIONGOAL_IMPRESSION': '展示量',
                'OPTIMIZATIONGOAL_CLICK': '点击量',
                'OPTIMIZATIONGOAL_CONVERSION': '转化量'
            };
            return goalMap[goal] || goal;
        },

        getSiteSetText(siteSet) {
            if (!siteSet || !Array.isArray(siteSet)) return '';

            const siteMap = {
                'SITE_SET_MOMENTS': '朋友圈',
                'SITE_SET_SEARCH_SCENE': '搜索场景',
                'SITE_SET_ARTICLE': '公众号文章'
            };

            return siteSet.map(site => siteMap[site] || site).join('、');
        },

        formatBudget(budget) {
            if (budget >= 10000) {
                return `¥${(budget / 10000).toFixed(1)}万`;
            }
            return `¥${budget}`;
        },

        formatTime(time) {
            if (!time) return '-';
            return new Date(time).toLocaleString('zh-CN');
        },

        formatTimestamp(timestamp) {
            if (!timestamp) return '-';
            return new Date(timestamp * 1000).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
}

.sync-section {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
}

.sync-status {
    display: flex;
    gap: 20px;
    background: #f8fafc;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
}

.status-item {
    display: flex;
    align-items: center;

    .label {
        color: #606266;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        margin-left: 4px;

        &.success {
            color: #67c23a;
        }

        &.fail {
            color: #f56c6c;
        }
    }
}

.stats-section {
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        font-size: 24px;
        color: white;

        &.total {
            background: linear-gradient(135deg, #409EFF, #67C2EF);
        }

        &.normal {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.pending {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }

        &.rejected {
            background: linear-gradient(135deg, #F56C6C, #F78989);
        }

        &.active {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.suspended {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }
    }

    .stat-content {
        flex: 1;

        .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #303133;
            margin-bottom: 4px;
        }

        .stat-label {
            font-size: 14px;
            color: #909399;
        }
    }
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

/* 空值样式 */
.empty-value {
    color: #909399;
    font-style: italic;
}

/* 广告管理弹窗样式 */
.ad-dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #ebeef5;
}

.ad-account-info {
    h3 {
        margin: 0 0 8px 0;
        color: #1f2937;
        font-size: 18px;
    }

    .account-details {
        display: flex;
        gap: 20px;
        font-size: 14px;
        color: #606266;

        span {
            background: #f5f7fa;
            padding: 4px 8px;
            border-radius: 4px;
        }
    }
}

.ad-sync-section {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
}

.ad-sync-status {
    display: flex;
    gap: 20px;
    background: #f8fafc;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
}

.ad-stats-section {
    margin-bottom: 24px;
}

.ad-search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .settings-form {
        max-width: 100%;
        padding: 20px;
    }

    .header-section {
        flex-direction: column;
        gap: 16px;
    }

    .sync-section {
        align-items: flex-start;
    }

    .search-bar {
        flex-wrap: wrap;
        gap: 10px;
    }

    .ad-dialog-header {
        flex-direction: column;
        gap: 16px;
    }

    .ad-sync-section {
        align-items: flex-start;
    }
}

/* 定向推广样式 */
.preview-logs-section {
    margin-top: 24px;
    border-top: 1px solid #ebeef5;
    padding-top: 16px;
}

.preview-logs-section h4 {
    margin: 0 0 16px 0;
    color: #1f2937;
    font-size: 16px;
}

.logs-list {
    max-height: 400px;
    overflow-y: auto;
}

.log-item {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fafafa;
}

.log-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.log-time {
    font-size: 12px;
    color: #909399;
}

.log-status.success {
    color: #67c23a;
    font-weight: 500;
}

.log-status.error {
    color: #f56c6c;
    font-weight: 500;
}

.log-content .label {
    font-weight: 500;
    color: #606266;
    margin-right: 8px;
}

.user-list,
.failed-users {
    margin-bottom: 8px;
}

.user-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.user-tag {
    cursor: pointer;
    transition: all 0.3s;
}

.user-tag:hover {
    opacity: 0.8;
}

.copy-icon {
    margin-left: 4px;
    font-size: 12px;
}

.error-message {
    color: #f56c6c;
    font-size: 12px;
    margin-top: 8px;
    padding: 4px 8px;
    background: #fef0f0;
    border-radius: 4px;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

/* 定向推广弹窗样式 */
.logs-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;
}

.logs-total {
    font-size: 14px;
    color: #606266;
}

.logs-list {
    max-height: 400px;
    overflow-y: auto;
    min-height: 200px;
}

.log-item {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fafafa;
    transition: all 0.3s;
}

.log-item:hover {
    border-color: #409eff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.log-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.log-time {
    font-size: 12px;
    color: #909399;
}

.log-status.success {
    color: #67c23a;
    font-weight: 500;
}

.log-status.error {
    color: #f56c6c;
    font-weight: 500;
}

.log-content .label {
    font-weight: 500;
    color: #606266;
    margin-right: 8px;
    font-size: 13px;
}

.user-list,
.failed-users {
    margin-bottom: 8px;
}

.user-actions {
    margin: 8px 0;
}

.user-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.user-tag {
    cursor: pointer;
    transition: all 0.3s;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-tag:hover {
    opacity: 0.8;
    transform: translateY(-1px);
}

.copy-icon {
    margin-left: 4px;
    font-size: 12px;
}

.error-message {
    color: #f56c6c;
    font-size: 12px;
    margin-top: 8px;
    padding: 4px 8px;
    background: #fef0f0;
    border-radius: 4px;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

.user-count {
    font-size: 12px;
    color: #409eff;
    margin-top: 4px;
    font-weight: 500;
}

.dialog-actions {
    text-align: right;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
}

.empty-logs {
    padding: 40px 0;
}

/* 标签页样式优化 */
:deep(.el-tabs__content) {
    padding: 16px;
}

:deep(.el-tabs--border-card) {
    border: none;
    box-shadow: none;
}

:deep(.el-tabs--border-card > .el-tabs__header) {
    background-color: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
}

/* 创意管理样式 - 与广告列表保持一致 */
.creative-management {
    padding: 0;
}

.creative-sync-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #ebeef5;
}

.creative-sync-status {
    display: flex;
    gap: 20px;
    background: #f8fafc;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
}

.creative-stats-section {
    margin-bottom: 24px;
}

.creative-search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

/* 确保统计卡片样式一致 */
.creative-stats-section .stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.creative-stats-section .stat-icon {
    width: 50px;
    height: 50px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    font-size: 24px;
    color: white;
}

.creative-stats-section .stat-icon.total {
    background: linear-gradient(135deg, #409EFF, #67C2EF);
}

.creative-stats-section .stat-icon.active {
    background: linear-gradient(135deg, #67C23A, #85CE61);
}

.creative-stats-section .stat-icon.suspended {
    background: linear-gradient(135deg, #E6A23C, #EBB563);
}

.creative-stats-section .stat-icon.pending {
    background: linear-gradient(135deg, #909399, #A6A9AD);
}

.creative-stats-section .stat-content {
    flex: 1;
}

.creative-stats-section .stat-number {
    font-size: 24px;
    font-weight: 700;
    color: #303133;
    margin-bottom: 4px;
}

.creative-stats-section .stat-label {
    font-size: 14px;
    color: #909399;
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .creative-sync-section {
        flex-direction: column;
        gap: 16px;
    }

    .creative-search-bar {
        flex-wrap: wrap;
        gap: 10px;
    }
}

@media (max-width: 768px) {
    .creative-stats-section .el-col {
        margin-bottom: 16px;
    }

    .creative-search-bar .el-input,
    .creative-search-bar .el-select {
        width: 100% !important;
        margin-right: 0 !important;
        margin-bottom: 10px;
    }
}
</style>